import { z } from "zod";
import { addressFields, intlPhoneSchema, nameSchema, optionalEmailSchema, orgSlugSchema, phoneSchema, } from "./common.js";
import { APPROVAL_MODES } from "../constants/statuses.js";
/**
 * Super admin creates the organization and its first admin in one step.
 * The admin receives a temporary password by email (D-04).
 */
export const HIERARCHY_TIERS = [
    "depot",
    "sales_rep",
    "distributor",
    "sub_distributor",
    "counter",
    "karigar",
];
/**
 * Tiers an org can stack more than once, e.g. Distributor 1 → Distributor 2.
 * Level 1 sits highest; each level-n person reports to one level-(n-1)
 * person of the same role. Depot, counter and karigar stay single.
 */
export const CLONABLE_TIERS = ["sales_rep", "distributor", "sub_distributor"];
export const MAX_TIER_LEVELS = 3;
const levelCount = z.number().int().min(1).max(MAX_TIER_LEVELS);
export const tierLevelsSchema = z.object({
    sales_rep: levelCount.optional(),
    distributor: levelCount.optional(),
    sub_distributor: levelCount.optional(),
});
/** How many levels a tier has in an org; 1 when never cloned. */
export function levelsOf(levels, tier) {
    return levels?.[tier] ?? 1;
}
/* ── the chain as nodes (D-16) ───────────────────────────────────────── */
/** `distributor:2` — the key a role's name, permissions and place in the chain are stored under. */
export function roleKey(role, level) {
    return `${role}:${level ?? 1}`;
}
export const roleKeySchema = z
    .string()
    .regex(/^(sales_rep|distributor|sub_distributor):[1-3]$|^counter:1$/, "Unknown role.");
/** Names typed into the chain builder, keyed by `roleKey`. */
export const roleNamesSchema = z.record(roleKeySchema, nameSchema);
/**
 * One step of the chain. A clonable tier appears once per level
 * (`sales_rep:1`, `sales_rep:2`) so a copied level can sit anywhere —
 * Sales Rep 2 under a Distributor. Other tiers appear by name.
 */
export const chainNodeSchema = z
    .string()
    .regex(/^(depot|counter|karigar)$|^(sales_rep|distributor|sub_distributor):[1-3]$/, "Unknown chain step.");
export function isStaffNode(node) {
    return /^(sales_rep|distributor|sub_distributor):/.test(node);
}
export function nodeOf(node) {
    const [tier, level] = node.split(":");
    return { tier: tier, level: Number(level ?? 1) };
}
/** The chain before anyone moved a copy: each copy straight under the level above it. */
export function defaultChain(enabledTiers, tierLevels) {
    return enabledTiers.flatMap((tier) => CLONABLE_TIERS.includes(tier)
        ? Array.from({ length: levelsOf(tierLevels, tier) }, (_, i) => roleKey(tier, i + 1))
        : [tier]);
}
/**
 * The org's chain, top to bottom. The stored order wins while it still
 * matches the tiers and levels in use; anything else (an older org, or
 * levels changed without an order) falls back to the default.
 */
export function resolveChain(settings) {
    const tiers = settings?.enabledTiers?.length
        ? settings.enabledTiers
        : settings?.distributorEnabled
            ? ["sales_rep", "distributor", "counter", "karigar"]
            : ["sales_rep", "counter", "karigar"];
    const fallback = defaultChain(tiers, settings?.tierLevels);
    const stored = settings?.chainOrder;
    const same = stored && stored.length === fallback.length && new Set(stored).size === stored.length && fallback.every((n) => stored.includes(n));
    return same ? [...stored] : fallback;
}
/** What to save for a chain: the tiers in use, their level counts, and the order itself. */
export function chainToSettings(chain) {
    const enabledTiers = [];
    const tierLevels = {};
    for (const node of chain) {
        const { tier, level } = nodeOf(node);
        if (!enabledTiers.includes(tier))
            enabledTiers.push(tier);
        if (isStaffNode(node))
            tierLevels[tier] = Math.max(tierLevels[tier] ?? 1, level);
    }
    return { enabledTiers, tierLevels: tierLevels, chainOrder: [...chain] };
}
/**
 * Who a copied level reports to: the nearest sales role above it in the
 * chain — its own level 1 by default, a Distributor if it was moved under
 * one. Level 1 of every role reports to no one; null also means a copy has
 * no sales role above it at all, which a save refuses.
 */
export function reportsTo(chain, node) {
    if (!isStaffNode(node) || nodeOf(node).level === 1)
        return null;
    const at = chain.indexOf(node);
    // A tier the org has switched off keeps the original rule: the level above it.
    if (at === -1)
        return roleKey(nodeOf(node).tier, nodeOf(node).level - 1);
    for (let i = at - 1; i >= 0; i--)
        if (isStaffNode(chain[i]))
            return chain[i];
    return null;
}
/** Every role that reports, directly or not, to `node`. */
export function rolesBelow(chain, node) {
    const below = [];
    let frontier = [node];
    while (frontier.length > 0) {
        frontier = chain.filter((n) => !below.includes(n) && frontier.includes(reportsTo(chain, n) ?? ""));
        below.push(...frontier);
    }
    return below;
}
export const createOrganizationSchema = z.object({
    name: nameSchema,
    slug: orgSlugSchema,
    admin: z.object({
        name: nameSchema,
        email: optionalEmailSchema,
        /*
         * The one place a non-Indian number is accepted. An org admin signs in
         * with a password and never touches the karigar WhatsApp flows, so their
         * country code can vary without the `+91` assumptions elsewhere breaking.
         */
        phone: intlPhoneSchema,
    }),
    enabledTiers: z.array(z.enum(HIERARCHY_TIERS)).optional(),
    tierLevels: tierLevelsSchema.optional(),
    /** Names given to copied levels in the chain builder, e.g. `distributor:2` → "Super Stockist". */
    roleNames: roleNamesSchema.optional(),
    /** The chain top to bottom, copies included (`sales_rep:2` under `distributor:1`). */
    chainOrder: z.array(chainNodeSchema).max(24).optional(),
});
export const updateOrganizationSchema = z.object({
    name: nameSchema.optional(),
    enabledTiers: z.array(z.enum(HIERARCHY_TIERS)).optional(),
    tierLevels: tierLevelsSchema.optional(),
    /** Names given to copied levels in the chain builder, e.g. `distributor:2` → "Super Stockist". */
    roleNames: roleNamesSchema.optional(),
    /** The chain top to bottom, copies included (`sales_rep:2` under `distributor:1`). */
    chainOrder: z.array(chainNodeSchema).max(24).optional(),
    /** Modules the super admin makes available to this org (D-20). */
    features: z.object({ dispatch: z.boolean().optional() }).optional(),
});
export const setOrganizationStatusSchema = z.object({
    status: z.enum(["active", "suspended"]),
});
/** Org-level settings an org admin controls. */
export const orgSettingsSchema = z.object({
    karigarApprovalMode: z.enum(APPROVAL_MODES).optional(),
    /** Turns the dispatch module on, once the super admin has made it available (D-20). */
    dispatchEnabled: z.boolean().optional(),
    distributorEnabled: z.boolean().optional(),
    enabledTiers: z.array(z.enum(HIERARCHY_TIERS)).optional(),
    twoFactorEnabled: z.boolean().optional(),
    tierLevels: tierLevelsSchema.optional(),
    /** Names given to copied levels in the chain builder, e.g. `distributor:2` → "Super Stockist". */
    roleNames: roleNamesSchema.optional(),
    /** The chain top to bottom, copies included (`sales_rep:2` under `distributor:1`). */
    chainOrder: z.array(chainNodeSchema).max(24).optional(),
    commission: z
        .object({
        counterFixedPoints: z.number().min(0).optional(),
        distributorFixedPoints: z.number().refine((value) => value === 0, "Distributors do not earn QCoin").optional(),
        /** Retained for older clients; new distributor awards are disabled. */
        distributorLevelPoints: z.array(z.number().refine((value) => value === 0, "Distributors do not earn QCoin")).max(MAX_TIER_LEVELS).optional(),
    })
        .optional(),
    commissionSettings: z
        .object({
        counterCommissionPoints: z.number().min(0).optional(),
        distributorCommissionPoints: z.number().refine((value) => value === 0, "Distributors do not earn QCoin").optional(),
    })
        .optional(),
});
/**
 * `code` is absent on purpose: the server derives it from the org and the
 * place (see `depotCode` in `services/depotCode.ts`). A hand-typed code was
 * the one field with no right answer, and two people naming the same depot
 * differently is how a stock route ends up pointing at nothing.
 */
export const createDepotSchema = z.object({
    name: nameSchema,
    phone: phoneSchema,
    email: optionalEmailSchema,
    state: z.string().trim().min(1, "Choose a state").max(100),
    city: z.string().trim().min(1, "Enter a city or town").max(100),
    ...addressFields,
});
export const updateDepotSchema = z.object({
    name: nameSchema.optional(),
    phone: phoneSchema.optional(),
    email: optionalEmailSchema,
    state: z.string().trim().max(100).optional(),
    city: z.string().trim().max(100).optional(),
    ...addressFields,
    status: z.enum(["active", "inactive"]).optional(),
});
//# sourceMappingURL=organization.js.map